<template>
	<div
		v-if="visible"
		class="form-element"
		:data-form-key="item.key"
		:class="{'invalid': errors.length > 0}"
	>
		<FormLabel
			:item="item"
			:for-id="thisId"
			:show-optional-label="showOptionalLabel"
		/>

		<div v-if="item.type === 'upload'" :id="thisId">
			<FormUpload :attachments="modelValue as ClaimedUpload[]" :attrs="attributes as UploadConfig" />
		</div>

		<FormTextarea
			v-else-if="item.type === 'textarea'"
			:id="thisId"
			:model-value="modelValue as string"
			:name="item.key"
			v-bind="attributes"
			:limit="getMaxLength(item)"
			@update:model-value="$emit('update:modelValue', $event)"
		/>

		<FormCheckboxes
			v-else-if="item.type === 'checkbox'"
			:id="thisId"
			:name="item.key"
			:model-value="modelValue as string[]"
			:options="unref(item.options) ?? []"
			@update:model-value="$emit('update:modelValue', $event);"
		/>

		<FormRadioButtons
			v-else-if="item.type === 'radio'"
			:id="thisId"
			:model-value="modelValue as string"
			:name="item.key"
			:options="unref(item.options) ?? []"
			v-bind="attributes"
			@update:model-value="$emit('update:modelValue', $event);"
		/>

		<FormSelect
			v-else-if="item.type === 'select'"
			:id="thisId"
			:model-value="modelValue as (string | number)"
			:name="item.key"
			:options="unref(item.options) ?? []"
			@update:model-value="$emit('update:modelValue', $event)"
		/>

		<FormDivider
			v-else-if="item.type === 'divider'"
			id="thisId"
			v-bind="attributes"
		/>

		<FormInput
			v-else
			:id="thisId"
			:type="item.type as InputTypes"
			:name="item.key"
			:model-value="modelValue as (string | number)"
			v-bind="attributes"
			@update:model-value="$emit('update:modelValue', $event)"
		/>

		<FormError v-if="!hideError && errors.length > 0" :for="thisId" :errors="errors" />

		<TextLengthIndicator
			v-if="showTextLengthIndicator"
			:length="(modelValue as string ?? '').length"
			:max="getMaxLength(item)"
		/>
	</div>
</template>

<script setup lang="ts">
	import {computed, unref} from 'vue';
	import type {PropType} from 'vue';
	import type {ValidationRuleWithParams} from '@vuelidate/core';
	import FormInput from '~/components/forms/parts/FormInput.vue';
	import FormTextarea from '~/components/forms/parts/FormTextarea.vue';
	import FormError from '~/components/forms/parts/FormError.vue';
	import type {InputTypes} from '~/components/forms/sharedFormInputProps';
	import type {FormElementItem} from '~/lib/types/formTypes';
	import FormCheckboxes from '~/components/forms/parts/FormCheckboxes.vue';
	import FormSelect from '~/components/forms/parts/FormSelect.vue';
	import FormRadioButtons from '~/components/forms/parts/FormRadioButtons.vue';
	import FormUpload from '~/components/forms/parts/FormUpload.vue';
	import type {ClaimedUpload, UploadConfig} from '~/lib/types/uploads';
	import TextLengthIndicator from '~/components/forms/parts/TextLengthIndicator.vue';
	import FormDivider from '~/components/forms/parts/FormDivider.vue';
	import FormLabel from '~/components/forms/parts/FormLabel.vue';

	const props = defineProps({
		item: {
			type: Object as PropType<FormElementItem>,
			required: true
		},
		modelValue: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			type: null as unknown as PropType<string | number | string[] | undefined | null | Record<string, any> | boolean | ClaimedUpload[]>,
			default: ''
		},
		hideError: {
			type: Boolean,
			default: false
		},
		errors: {
			type: Array as PropType<string[]>,
			default: () => []
		},
		hideOptionalLabel: {
			type: Boolean,
			default: false
		}
	});

	defineEmits(['update:modelValue']);

	const visible = computed(() => props.item.visible === undefined || unref(props.item.visible) === true);

	const thisId = computed(() => {
		return `form-input-${props.item.key}`;
	});

	const attributes = computed(() => {
		return unref(props.item.attrs) ?? {};
	});

	const isRequired = computed(() => {
		const keys = Object.keys(props.item.validators ?? {});
		if (keys.length === 0) {
			return false;
		}

		const validationKeys = keys.filter((key) => !key.startsWith('$'));
		// Check for required, requiredIf and other custom methods which should start with required
		return validationKeys.some((key) => key.includes('required'));
	});

	const showOptionalLabel = computed(() => {
		return !isRequired.value && props.hideOptionalLabel !== true;
	});

	const getMaxLength = (item: FormElementItem) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return (item.validators?.maxLength as ValidationRuleWithParams<any, any> | undefined)?.$params?.max;
	};

	const showTextLengthIndicator = computed(() => {
		const types = ['text', 'textarea', 'password', 'email', 'search', 'tel', 'url'];
		return types.includes(props.item.type) && getMaxLength(props.item) !== undefined;
	});
</script>

<style scoped>
	.invalid {
		--input-border-color: var(--color-error);
	}

	.form-element {
		display: grid;
		grid-gap: 0;
		grid-column-gap: var(--spacing-2);
		width: 100%;

		container-type: inline-size;
		container-name: formElement;

		grid-auto-columns: 100%;

		&:has(.text-length-indicator) {
			grid-template-columns: 1fr auto;

			& > * {
				grid-column: 1 / -1;
			}

			.form-element-label {
				grid-column: 1 / 2;
				grid-row: 1 / 2;
			}

			.text-length-indicator {
				grid-column: 2 / 3;
				grid-row: 1 / 2;
				align-self: end;
			}
		}
	}

	@container (min-width: 620px) {
		.form-element {
			max-width: 500px;
		}
	}
</style>
